import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PieController,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend
);

const DecimalToPercentConverter = () => {
  const [input, setInput] = useState({
    decimal: '',
    result: '',
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInput(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const convertToPercent = () => {
    const { decimal } = input;
    
    // Validate input
    if (!decimal) {
      setError('Please enter a decimal value');
      return;
    }

    // Validate numeric input
    if (isNaN(decimal)) {
      setError('Please enter a valid decimal number');
      return;
    }

    // Convert to number
    const numericDecimal = parseFloat(decimal);

    // Check for valid range
    if (numericDecimal < 0 || numericDecimal > 1) {
      setError('Decimal value should be between 0 and 1');
      return;
    }

    // Calculate percentage
    const percent = (numericDecimal * 100).toFixed(2);

    // Create visualization data
    const remaining = 100 - numericDecimal * 100;
    setChartData({
      labels: ['Converted Value', 'Remaining'],
      datasets: [
        {
          data: [numericDecimal * 100, remaining],
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(230, 230, 230, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(230, 230, 230, 1)'
          ],
          borderWidth: 1,
        }
      ]
    });

    setResult({
      decimal: numericDecimal,
      percent,
      remaining: remaining.toFixed(2)
    });
  };

  const exampleData = {
    decimal: '0.75',
    percent: '75.00'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-green-100 to-blue-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Decimal to Percent Converter | Number Conversion Tool</title>
          <meta name="description" content="Convert decimal values to percentages with this simple calculator. Input a decimal number between 0 and 1 and get the equivalent percentage value." />
          <meta name="keywords" content="decimal to percent, decimal converter, percentage calculator, number conversion" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-green-600 mr-4">🔄</div>
            <h1 className="text-3xl font-bold text-gray-800">Decimal to Percent Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert decimal values to percentages with this simple calculator. Input a decimal number between 0 and 1 and get the equivalent percentage value. This tool helps visualize and understand the relationship between decimals and percentages.</p>
          </div>

          <div className="input-group mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Decimal Value:</label>
            <input
              type="text"
              name="decimal"
              value={input.decimal}
              onChange={handleInputChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="Enter decimal value (e.g., 0.75)"
            />
          </div>

          <button
            onClick={convertToPercent}
            className="w-full py-3 px-4 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert to Percentage
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Result</h3>
                  <p className="text-lg">Decimal Value: {result.decimal}</p>
                  <p className="text-lg font-bold mt-4">Percentage: {result.percent}%</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Details</h3>
                  <p className="text-lg">Method: Multiply by 100</p>
                  <p className="text-lg">Formula: decimal × 100 = percentage</p>
                </div>
              </div>

              {chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Pie
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        title: {
                          display: true,
                          text: 'Percentage Visualization'
                        },
                        legend: {
                          position: 'bottom'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Breakdown</h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <p className="text-gray-700">
                      Decimal value: {result.decimal}
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <p className="text-gray-700">
                      Multiply by 100: {result.decimal} × 100 = {result.percent}%
                    </p>
                  </div>
                  <div className="p-4 bg-white rounded-lg shadow-inner">
                    <p className="text-gray-700">
                      Remaining percentage: {result.remaining}%
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-green-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Decimal Value: {exampleData.decimal}</li>
                <li>Percentage: {exampleData.percent}%</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Multiply the decimal by 100:
                  <ul className="list-disc pl-6">
                    <li>0.75 × 100 = 75%</li>
                  </ul>
                </li>
                <li>Add the percentage symbol</li>
                <li>The result is 75%</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-green-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Decimal to Percent Converter:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Decimal:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input a decimal value between 0 and 1</li>
                </ul>
              </li>
              <li><strong>Click Convert:</strong> The calculator will convert the decimal to percentage</li>
              <li><strong>Review Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>View the percentage value</li>
                  <li>Check the conversion breakdown</li>
                  <li>Visualize the percentage with the pie chart</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-green-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Use for academic grades (e.g., converting GPA to percentage)</li>
                <li>Convert probabilities (values between 0 and 1)</li>
                <li>Understand financial metrics like interest rates</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Decimal to Percent Conversion <span className="text-green-600">📊</span></h2>
          <div className="text-gray-600">
            <p>
              Decimal to percent conversion is a fundamental mathematical operation that helps express values as parts of 100. This is useful because:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Intuitive Understanding:</strong> Percentages are easier to understand than decimals for most people</li>
              <li><strong>Standardization:</strong> Percentages provide a common scale for comparison</li>
              <li><strong>Visualization:</strong> Easier to create visual representations</li>
              <li><strong>Everyday Applications:</strong> Used in finance, statistics, education, and more</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-green-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Range Check:</strong> Ensure decimal values are between 0 and 1</li>
              <li><strong>Decimal Places:</strong> Round percentages to appropriate decimal places</li>
              <li><strong>Context:</strong> Consider the context when interpreting percentages</li>
              <li><strong>Reverse Conversion:</strong> Remember that percentage ÷ 100 = decimal</li>
            </ul>
            <p className="italic mt-4">
              Note: To convert a percentage back to decimal, simply divide by 100. For example, 75% ÷ 100 = 0.75.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default DecimalToPercentConverter;